'use client';

/**
 * @fileoverview ChatPanelProvider — per-project UI state for the
 * "Chat with Your Story" side panel.
 *
 * Tracks three pieces of ephemeral UI state:
 *   - `isOpen` — is the panel currently shown?
 *   - `width` — user-resized width in px (persists in localStorage).
 *   - `activeSessionId` — the chat session the panel is currently
 *     displaying; `null` means "start a new chat".
 *
 * State is scoped per-project by keying on `activeProject.id`. When the
 * active project changes, previous state is cleared. Width is persisted
 * globally so a user's preferred panel width follows them across
 * projects — it's a physical-screen preference, not a per-story one.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useActiveProject } from './active-project-context';

// =============================================================================
// CONSTANTS
// =============================================================================

const DEFAULT_WIDTH = 420;
const MIN_WIDTH = 320;
const MAX_WIDTH = 720;
const WIDTH_STORAGE_KEY = 'chat-panel:width';

// =============================================================================
// CONTEXT
// =============================================================================

interface ChatPanelContextValue {
  isOpen: boolean;
  width: number;
  activeSessionId: string | null;

  open: () => void;
  close: () => void;
  toggle: () => void;
  setWidth: (width: number) => void;
  setActiveSessionId: (id: string | null) => void;

  /** Clamped bounds for the drag handle. */
  minWidth: number;
  maxWidth: number;
}

const ChatPanelContext = createContext<ChatPanelContextValue | undefined>(
  undefined,
);

// =============================================================================
// PROVIDER
// =============================================================================

export function ChatPanelProvider({ children }: { children: ReactNode }) {
  const { activeProject } = useActiveProject();
  const projectId = activeProject?.id ?? null;

  // Per-project state. Reset when the active project changes.
  const [isOpen, setIsOpen] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

  useEffect(() => {
    setIsOpen(false);
    setActiveSessionId(null);
  }, [projectId]);

  // Width persists globally — it's a screen-size preference.
  const [width, setWidthState] = useState<number>(DEFAULT_WIDTH);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(WIDTH_STORAGE_KEY);
    if (!stored) return;
    const parsed = Number(stored);
    if (Number.isFinite(parsed) && parsed >= MIN_WIDTH && parsed <= MAX_WIDTH) {
      setWidthState(parsed);
    }
  }, []);

  const setWidth = (next: number) => {
    const clamped = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, next));
    setWidthState(clamped);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(WIDTH_STORAGE_KEY, String(clamped));
    }
  };

  const value = useMemo<ChatPanelContextValue>(
    () => ({
      isOpen,
      width,
      activeSessionId,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false),
      toggle: () => setIsOpen((prev) => !prev),
      setWidth,
      setActiveSessionId,
      minWidth: MIN_WIDTH,
      maxWidth: MAX_WIDTH,
    }),
    [isOpen, width, activeSessionId],
  );

  return (
    <ChatPanelContext.Provider value={value}>
      {children}
    </ChatPanelContext.Provider>
  );
}

// =============================================================================
// HOOK
// =============================================================================

export function useChatPanel(): ChatPanelContextValue {
  const ctx = useContext(ChatPanelContext);
  if (!ctx) {
    throw new Error('useChatPanel must be used within a ChatPanelProvider');
  }
  return ctx;
}
